/**
 * @fileoverview React hook for reading and writing the signed-in
 * user's personal preferences document in Firestore.
 *
 * Preferences live at `userPreferences/{uid}` and include UI state
 * that should persist per-user across sessions and devices — default
 * view modes, default sort orders, avatar URL, etc.
 *
 * Writes go directly from the client to Firestore; security rules
 * restrict each user to reading/writing only their own document,
 * so no Firebase Function is needed. Reads use a one-shot `getDoc`
 * on mount (not a live listener) since preferences change rarely.
 *
 * @requires firebase/firestore
 * @requires firebase/auth
 * @requires ../firebase
 */

import { useCallback, useEffect, useState } from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";

/**
 * Default preference values used when no userPreferences document
 * exists yet, or when specific keys are missing from the stored doc.
 *
 * @constant {Object}
 */
export const DEFAULT_PREFERENCES = {
  projectsDefaultView: "card",
  projectsDefaultSort: "startDate",
  projectsSortDirection: "desc",
  clientsDefaultView: "card",
  clientsDefaultSort: "recordCreated",
  clientsSortDirection: "desc",
  vendorsDefaultView: "card",
  vendorsDefaultSort: "vendorName",
  vendorsSortDirection: "asc",
  vendorsShowInactive: false,
  avatarUrl: null,
  // Client portal onboarding tour — per-page "has been seen" flags.
  // Each page's guided tour auto-runs once; these persist completion
  // per-user so returning visitors aren't shown it again.
  onboardingDashboardSeen: false,
  onboardingUpdatesSeen: false,
  onboardingSelectionsSeen: false,
  onboardingChangeOrdersSeen: false,
  onboardingDocumentsSeen: false,
  onboardingProfileSeen: false,
};

/**
 * Hook for reading and writing the signed-in user's preferences.
 *
 * On mount (and whenever the auth state changes), reads the user's
 * `userPreferences/{uid}` document and merges the stored fields
 * over DEFAULT_PREFERENCES. `updatePreferences(fields)` writes the
 * given fields back to Firestore with `merge: true` and optimistically
 * updates local state so the UI reflects the change immediately.
 *
 * @returns {Object} Hook state and actions
 * @property {Object} preferences - Merged preferences object
 * @property {Function} updatePreferences - Async updater:
 *   `(fields: Object) => Promise<void>`
 * @property {boolean} isLoading - True until the initial load
 *   resolves (either successfully or with an error)
 * @property {Error|null} error - Last load/save error, if any
 *
 * @example
 * const { preferences, updatePreferences } = useUserPreferences();
 *
 * // Persist a new default view:
 * updatePreferences({ projectsDefaultView: "list" });
 */
export function useUserPreferences() {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uid, setUid] = useState(auth.currentUser?.uid ?? null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid ?? null);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!uid) {
      setPreferences(DEFAULT_PREFERENCES);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);
    setError(null);

    const unsub = onSnapshot(
      doc(db, "userPreferences", uid),
      (snap) => {
        const stored = snap.exists() ? snap.data() : {};
        setPreferences({ ...DEFAULT_PREFERENCES, ...stored });
        setIsLoading(false);
      },
      (err) => {
        console.error("[useUserPreferences] Snapshot error:", err);
        setError(err);
        setPreferences(DEFAULT_PREFERENCES);
        setIsLoading(false);
      },
    );

    return unsub;
  }, [uid]);

  /**
   * Merge-save the given fields into `userPreferences/{uid}`.
   * @param {Object} fields - Partial preference fields to write
   * @returns {Promise<void>}
   */
  const updatePreferences = useCallback(
    async (fields) => {
      if (!uid) {
        throw new Error("Cannot update preferences — no signed-in user");
      }
      if (!fields || typeof fields !== "object") {
        throw new Error("fields must be an object");
      }

      try {
        await setDoc(doc(db, "userPreferences", uid), fields, { merge: true });
        setPreferences((prev) => ({ ...prev, ...fields }));
      } catch (err) {
        console.error("[useUserPreferences] Save failed:", err);
        setError(err);
        throw err;
      }
    },
    [uid],
  );

  return {
    preferences,
    updatePreferences,
    isLoading,
    error,
  };
}

export default useUserPreferences;
